import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Eye, X, Folder, Tag, Lock, Globe, Building2, Key } from 'lucide-react';
import { documentationAPI, adminCompanyAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import PasswordPicker from '../../components/documentation/PasswordPicker';
import { isInternalRole } from '../../utils/roles.js';

export default function ArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isNew = !id || id === 'new';

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    contentMarkdown: '',
    excerpt: '',
    folderId: null,
    companyId: null,
    visibility: 'private',
    status: 'draft',
    isTemplate: false,
    tags: [],
    changeSummary: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showPasswordPicker, setShowPasswordPicker] = useState(false);

  const isInternal = isInternalRole(user?.role);

  // Redirect if not internal user
  useEffect(() => {
    if (!isInternal) {
      navigate('/documentation');
    }
  }, [isInternal, navigate]);

  // Fetch existing article if editing
  const { data: articleData, isLoading: articleLoading } = useQuery({
    queryKey: ['documentation', 'article', id],
    queryFn: () => documentationAPI.getArticle(id),
    enabled: !isNew,
  });

  // Fetch folders for dropdown
  const { data: foldersData } = useQuery({
    queryKey: ['documentation', 'folders', 'tree'],
    queryFn: () => documentationAPI.getFolderTree(),
  });

  // Fetch tag suggestions
  const { data: tagSuggestions } = useQuery({
    queryKey: ['documentation', 'tags', 'autocomplete', tagInput],
    queryFn: () => documentationAPI.autocompleteTags(tagInput, 5),
    enabled: tagInput.length >= 2,
  });

  // Fetch companies for company selector
  const { data: companiesData } = useQuery({
    queryKey: ['admin', 'companies', 'list'],
    queryFn: () => adminCompanyAPI.list({ limit: 500 }),
  });

  // Populate form when editing
  useEffect(() => {
    if (articleData?.data?.article) {
      const article = articleData.data.article;
      setFormData({
        title: article.title || '',
        content: article.content || '',
        contentMarkdown: article.content_markdown || '',
        excerpt: article.excerpt || '',
        folderId: article.folder_id || null,
        companyId: article.company_id || null,
        visibility: article.visibility || 'private',
        status: article.status || 'draft',
        isTemplate: article.is_template || false,
        tags: article.tags?.map(t => typeof t === 'string' ? t : t.name) || [],
        changeSummary: '',
      });
    }
  }, [articleData]);

  // Flatten folder tree for select
  const flattenFolders = (folders, level = 0) => {
    let result = [];
    for (const folder of folders) {
      result.push({ ...folder, level });
      if (folder.children?.length > 0) {
        result = result.concat(flattenFolders(folder.children, level + 1));
      }
    }
    return result;
  };

  const folders = flattenFolders(foldersData?.data?.tree || []);
  const companies = companiesData?.data?.companies || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => documentationAPI.createArticle(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['documentation'] });
      navigate(`/documentation/articles/${response.data.article.id}`);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to create article');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => documentationAPI.updateArticle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation'] });
      navigate(`/documentation/articles/${id}`);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to update article');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    const data = {
      title: formData.title.trim(),
      content: formData.content,
      contentMarkdown: formData.contentMarkdown,
      excerpt: formData.excerpt.trim(),
      folderId: formData.folderId || null,
      companyId: formData.companyId || null,
      visibility: formData.visibility,
      status: formData.status,
      isTemplate: formData.isTemplate,
      tags: formData.tags,
    };

    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate({
        ...data,
        changeSummary: formData.changeSummary.trim(),
      });
    }
  };

  const handleSaveAndPublish = (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, status: 'published' }));
    setTimeout(() => {
      document.getElementById('article-form').dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
    }, 0);
  };

  const addTag = (tag) => {
    const tagName = tag.trim().toLowerCase();
    if (tagName && !formData.tags.includes(tagName)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagName] }));
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      removeTag(formData.tags[formData.tags.length - 1]);
    }
  };

  if (!isInternal) {
    return null;
  }

  if (!isNew && articleLoading) {
    return (
      <div className="animate-fade-in">
        <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-64 mb-6 animate-pulse" />
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="space-y-4">
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
            <div className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={isNew ? '/documentation' : `/documentation/articles/${id}`}
            className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {isNew ? 'New Article' : 'Edit Article'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <form id="article-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter article title..."
                className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </div>

            {/* Excerpt */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Excerpt
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                placeholder="Brief summary of the article..."
                rows={2}
                className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Content */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Content
                </label>
                {!showPreview && (
                  <button
                    type="button"
                    onClick={() => setShowPasswordPicker(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                  >
                    <Key className="w-3.5 h-3.5" />
                    Insert Password
                  </button>
                )}
              </div>
              {showPreview ? (
                <div
                  className="prose prose-neutral dark:prose-invert max-w-none min-h-[300px] p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-900"
                  dangerouslySetInnerHTML={{ __html: formData.content || '<p>No content yet.</p>' }}
                />
              ) : (
                <textarea
                  id="content-editor"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your article content here... (HTML supported)"
                  rows={15}
                  className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent font-mono text-sm"
                />
              )}
              <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                You can use HTML for formatting. Use the "Insert Password" button to embed password references.
              </p>
            </div>

            {/* Change Summary (edit only) */}
            {!isNew && (
              <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Change Summary
                </label>
                <input
                  type="text"
                  value={formData.changeSummary}
                  onChange={(e) => setFormData(prev => ({ ...prev, changeSummary: e.target.value }))}
                  placeholder="Brief description of changes made..."
                  className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-4">Publish</h3>

              {/* Status */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Visibility */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Visibility
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'private', icon: Lock, label: 'Internal Only', desc: 'Only staff can view' },
                    { value: 'company', icon: Building2, label: 'Company', desc: 'Shared with specific company' },
                    { value: 'public', icon: Globe, label: 'Public', desc: 'All customers can view' },
                  ].map(({ value, icon: Icon, label, desc }) => (
                    <label
                      key={value}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors
                        ${formData.visibility === value
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                          : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                        }`}
                    >
                      <input
                        type="radio"
                        name="visibility"
                        value={value}
                        checked={formData.visibility === value}
                        onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-neutral-500" />
                          <span className="font-medium text-neutral-900 dark:text-white">{label}</span>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Company selector - show when visibility is 'company' */}
              {formData.visibility === 'company' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Company *
                  </label>
                  <select
                    value={formData.companyId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyId: e.target.value ? parseInt(e.target.value) : null }))}
                    className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a company...</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.cw_company_id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    This article will be visible to users from this company
                  </p>
                </div>
              )}

              {/* Template checkbox */}
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={formData.isTemplate}
                  onChange={(e) => setFormData(prev => ({ ...prev, isTemplate: e.target.checked }))}
                  className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  Save as template
                </span>
              </label>

              {/* Action buttons */}
              <div className="flex flex-col gap-2">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
                {formData.status !== 'published' && (
                  <button
                    type="button"
                    onClick={handleSaveAndPublish}
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Save & Publish
                  </button>
                )}
              </div>
            </div>

            {/* Folder */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                <Folder className="w-4 h-4" />
                Folder
              </h3>
              <select
                value={formData.folderId || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, folderId: e.target.value || null }))}
                className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">No folder (root)</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>
                    {'  '.repeat(folder.level)}{folder.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 dark:bg-neutral-700 rounded text-sm text-neutral-700 dark:text-neutral-300"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add tags..."
                  className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                {tagSuggestions?.data?.tags?.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg">
                    {tagSuggestions.data.tags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => addTag(tag.name)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
                      >
                        {tag.name}
                        <span className="text-neutral-400 ml-2">({tag.article_count})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                Press Enter to add a tag
              </p>
            </div>
          </div>
        </div>
      </form>

      {/* Password Picker Modal */}
      <PasswordPicker
        isOpen={showPasswordPicker}
        onClose={() => setShowPasswordPicker(false)}
        onSelect={(embedSyntax) => {
          // Insert the password reference at cursor position or end of content
          const textarea = document.getElementById('content-editor');
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const before = formData.content.slice(0, start);
            const after = formData.content.slice(end);
            setFormData(prev => ({
              ...prev,
              content: before + embedSyntax + after
            }));
            // Focus back to textarea after a short delay
            setTimeout(() => {
              textarea.focus();
              const newCursorPos = start + embedSyntax.length;
              textarea.setSelectionRange(newCursorPos, newCursorPos);
            }, 100);
          } else {
            // Fallback: append to end
            setFormData(prev => ({
              ...prev,
              content: prev.content + embedSyntax
            }));
          }
        }}
        companyId={formData.companyId}
      />
    </div>
  );
}
